// src/components/Prospecting/SiteConsultants/SiteConsultants.js

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import SiteConsultantsGrid from "./SiteConsultantsGrid";
import {
  Box,
  Typography,
  Alert,
  LinearProgress,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SaveIcon from "@mui/icons-material/Save";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SiteConsultantFilters from "./SiteConsultantFilters";
import BrandPagination from "../../../layouts/Pagination";
import api from "../../../services/api/axios";
import { useAuth } from "../../../providers/AuthProvider";
import SearchBar from "../../../shared/components/SearchBar";
import {
  downloadBlobFile,
  normalizeSiteConsultant,
} from "./SiteConsultantHelpers";
import {
  ACTIONS_HEADER_BTN_SX,
  ACTION_MENU_PAPER_SX,
} from "../../../theme/buttonstyles";

const BRAND_BLUE = "#00336C";
const PAGE_BG = "#f7f9fc";
const PAGE_SIZE = 54;

const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
  background: "#ffffff",
  overflow: "visible",
};

export default function SiteConsultants() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [consultants, setConsultants] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    countries: [],
    location_index: {},
    industries: [],
  });
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  const [countryFilter, setCountryFilter] = useState([]);
  const [stateFilter, setStateFilter] = useState([]);
  const [industryFilter, setIndustryFilter] = useState([]);
  const [cityFilter, setCityFilter] = useState([]);

  const [revealedState, setRevealedState] = useState({});
  const [flippedCards, setFlippedCards] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedConsultantsById, setSelectedConsultantsById] = useState({});
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const actionsMenuOpen = Boolean(actionsAnchorEl);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)),
    [totalCount],
  );

  const toggleReveal = (dynamicsId, field) => {
    setRevealedState((prev) => ({
      ...prev,
      [dynamicsId]: {
        ...prev[dynamicsId],
        [field]: !prev[dynamicsId]?.[field],
      },
    }));
  };

  const toggleFlip = (dynamicsId) => {
    setFlippedCards((prev) => ({ ...prev, [dynamicsId]: !prev[dynamicsId] }));
  };

  const toggleSelect = (dynamicsId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const selectedConsultant = consultants.find(
        (item) => item.dynamics_id === dynamicsId,
      );

      if (next.has(dynamicsId)) {
        next.delete(dynamicsId);
        setSelectedConsultantsById((current) => {
          const updated = { ...current };
          delete updated[dynamicsId];
          return updated;
        });
      } else {
        next.add(dynamicsId);
        if (selectedConsultant) {
          setSelectedConsultantsById((current) => ({
            ...current,
            [dynamicsId]: selectedConsultant,
          }));
        }
      }

      return next;
    });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/site-consultants", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search: appliedSearch || undefined,
          country: countryFilter.length ? countryFilter.join(",") : undefined,
          state: stateFilter.length ? stateFilter.join(",") : undefined,
          city: cityFilter.length ? cityFilter.join(",") : undefined,
          industry: industryFilter.length
            ? industryFilter.join(",")
            : undefined,
          limit: PAGE_SIZE,
          offset: Math.max(page - 1, 0) * PAGE_SIZE,
        },
      });

      const parsed = (response.data.items || []).map(normalizeSiteConsultant);
      setConsultants(parsed);
      setTotalCount(
        response.data.total || response.data.count || parsed.length,
      );
      setFilterOptions(
        response.data.filters || {
          countries: [],
          location_index: {},
          industries: [],
        },
      );
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err.message ||
          "Failed to load site consultants",
      );
    } finally {
      setLoading(false);
    }
  }, [
    appliedSearch,
    cityFilter,
    countryFilter,
    industryFilter,
    page,
    stateFilter,
    token,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleSearchEnter = () => {
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const handleExport = async () => {
    try {
      const response = await api.post(
        "/site-consultants/export",
        selectedIds.size > 0
          ? { rows: Object.values(selectedConsultantsById) }
          : {
              filters: {
                search: appliedSearch || undefined,
                country: countryFilter.length
                  ? countryFilter.join(",")
                  : undefined,
                state: stateFilter.length ? stateFilter.join(",") : undefined,
                city: cityFilter.length ? cityFilter.join(",") : undefined,
                industry: industryFilter.length
                  ? industryFilter.join(",")
                  : undefined,
              },
            },
        { responseType: "blob" },
      );
      downloadBlobFile(new Blob([response.data]), "SiteConsultants.xlsx");
    } catch {
      setError("Export failed");
    }
  };

  const handleSaveSelected = async () => {
    if (selectedIds.size === 0) return;

    const items = Object.values(selectedConsultantsById).map((c) => ({
      dynamics_id: c.dynamics_id,
      name: c.name,
      company: c.company,
      title: c.title,
      city: c.city,
      state: c.state,
      country: c.country,
      industries: c.industries,
      normalized_industry: c.normalized_industry,
      email: c.email,
      office_phone: c.office_phone,
      mobile_phone: c.mobile_phone,
      linkedin: c.linkedin,
      website: c.website,
    }));

    try {
      await api.post("/user-site-consultants", items);
      setSelectedIds(new Set());
      setSelectedConsultantsById({});
    } catch {
      setError("Failed to save site consultants");
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: PAGE_BG,
        pb: 4,
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 1.75,
            flexWrap: "wrap",
          }}
        >
          {/* Left: icon + title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: "#648315",
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <PeopleAltIcon sx={{ fontSize: 19 }} />
            </Box>
            <Typography
              sx={{
                fontSize: "1.05rem",
                fontWeight: 800,
                color: BRAND_BLUE,
                letterSpacing: "-0.01em",
                whiteSpace: "nowrap",
              }}
            >
              Site Consultants
            </Typography>
          </Box>

          {/* Center: search */}
          <Box
            sx={{ flexGrow: 1, minWidth: 0, width: { xs: "100%", md: "auto" } }}
          >
            <SearchBar
              value={search}
              onChange={setSearch}
              onEnter={handleSearchEnter}
              placeholder="Search Site Consultant Name, Company, Location, or Industry..."
            />
          </Box>

          {/* Right: actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(e) => setActionsAnchorEl(e.currentTarget)}
              sx={ACTIONS_HEADER_BTN_SX}
            >
              Actions
            </Button>

            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsMenuOpen}
              onClose={() => setActionsAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
            >
              <MenuItem
                onClick={() => {
                  setActionsAnchorEl(null);
                  handleSaveSelected();
                }}
                disabled={selectedIds.size === 0}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "#0F766E" }}>
                  <SaveIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Save Selected Consultants</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setActionsAnchorEl(null);
                  navigate("/my-site-consultants");
                }}
              >
                <ListItemText>My Site Consultants</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setActionsAnchorEl(null);
                  handleExport();
                }}
                disabled={totalCount === 0}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "#0284C7" }}>
                  <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {loading && <LinearProgress />}
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, mt: 2 }}>
        {/* Filters */}
        <Box
          sx={{
            ...CARD_SX,
            mb: 2,
            px: { xs: 2, sm: 2.5 },
            pt: { xs: 1.5, sm: 2 },
            pb: 0,
          }}
        >
          <SiteConsultantFilters
            consultants={consultants}
            filterOptions={filterOptions}
            countryFilter={countryFilter}
            setCountryFilter={(values) => {
              setPage(1);
              setCountryFilter(values);
            }}
            stateFilter={stateFilter}
            setStateFilter={(values) => {
              setPage(1);
              setStateFilter(values);
            }}
            cityFilter={cityFilter}
            setCityFilter={(values) => {
              setPage(1);
              setCityFilter(values);
            }}
            industryFilter={industryFilter}
            setIndustryFilter={(values) => {
              setPage(1);
              setIndustryFilter(values);
            }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <SiteConsultantsGrid
          consultants={consultants}
          loading={loading}
          selectedIds={selectedIds}
          toggleSelect={toggleSelect}
          revealedState={revealedState}
          toggleReveal={toggleReveal}
          flippedCards={flippedCards}
          toggleFlip={toggleFlip}
        />

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", pb: 3, mt: 2 }}>
            <BrandPagination
              page={page}
              count={totalPages}
              onChange={(_, value) => setPage(value)}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
